import {
  AlertCircle,
  AlertTriangle,
  CheckCircle,
  ChevronRight,
  Clock,
  Loader,
} from 'lucide-react-native';
import { type ReactNode, useState } from 'react';
import { Platform, Pressable, StyleSheet, View, type ViewStyle } from 'react-native';
import { FadeIn } from '../../../components/animations';
import { Text } from '../../../components/primitives';
import type { WorkflowExecutionRecord } from '../../../lib/workflowsApi';
import { colors } from '../../../theme/colors';
import { tokens } from '../../../theme/tokens';

export type ExecutionStatus = 'success' | 'partial_success' | 'failed' | 'running';

export interface Execution {
  id: string;
  workflowName: string;
  status: ExecutionStatus;
  statusLabel: string;
  timestamp: string;
  duration: string;
  source?: WorkflowExecutionRecord;
}

interface ExecutionRowProps {
  execution: Execution;
  index: number;
  onPress?: (execution: Execution) => void;
  accessibilityHint?: string;
}

const statusConfig: Record<ExecutionStatus, { icon: ReactNode; color: string; bg: string }> = {
  success: {
    icon: <CheckCircle size={14} color={colors.status.success.main} strokeWidth={2} />,
    color: colors.status.success.main,
    bg: colors.status.success.bg,
  },
  partial_success: {
    icon: <AlertTriangle size={14} color={colors.status.warning.main} strokeWidth={2} />,
    color: colors.status.warning.main,
    bg: colors.status.warning.bg,
  },
  failed: {
    icon: <AlertCircle size={14} color={colors.status.error.main} strokeWidth={2} />,
    color: colors.status.error.main,
    bg: colors.status.error.bg,
  },
  running: {
    icon: <Loader size={14} color={colors.status.warning.main} strokeWidth={2} />,
    color: colors.status.warning.main,
    bg: colors.status.warning.bg,
  },
};

export function ExecutionRow({
  execution,
  index,
  onPress,
  accessibilityHint,
}: Readonly<ExecutionRowProps>) {
  const [isHovered, setIsHovered] = useState(false);
  const status = statusConfig[execution.status];

  const webHoverStyle = Platform.select({
    web: {
      transition: 'background-color 0.15s ease, border-color 0.15s ease',
      cursor: onPress ? 'pointer' : 'default',
      ...(isHovered && {
        backgroundColor: colors.surface.rowHover,
        borderColor: status.color,
      }),
    } as ViewStyle,
    default: {},
  });

  const hoverProps = Platform.select({
    web: {
      onMouseEnter: () => setIsHovered(true),
      onMouseLeave: () => setIsHovered(false),
    },
    default: {},
  });

  return (
    <FadeIn delay={index * 40} translateY={4}>
      <Pressable
        testID={`workflow_execution_row_${execution.id}`}
        accessibilityRole={onPress ? 'button' : undefined}
        accessibilityLabel={`${execution.workflowName}, ${execution.statusLabel}, ${execution.timestamp}, ${execution.duration}`}
        accessibilityHint={onPress ? accessibilityHint : undefined}
        onPress={onPress ? () => onPress(execution) : undefined}
        style={({ pressed }) => [styles.row, webHoverStyle, pressed && styles.rowPressed]}
        {...hoverProps}
      >
        <View style={styles.iconWrap}>{status.icon}</View>

        <View style={styles.body}>
          <Text variant="body" color={colors.text.primary} numberOfLines={1} style={styles.name}>
            {execution.workflowName}
          </Text>
          <View style={styles.metaRow}>
            <Clock size={11} color={colors.text.tertiary} strokeWidth={1.8} />
            <Text style={styles.metaText} numberOfLines={1}>
              {execution.timestamp}
            </Text>
            <Text style={styles.metaDot}>·</Text>
            <Text style={styles.metaText} numberOfLines={1}>
              {execution.duration}
            </Text>
          </View>
        </View>

        <View style={[styles.pill, { backgroundColor: status.bg, borderColor: status.color }]}>
          <Text style={[styles.pillText, { color: status.color }]} numberOfLines={1}>
            {execution.statusLabel}
          </Text>
        </View>
        {onPress && <ChevronRight size={15} color={colors.text.tertiary} strokeWidth={1.8} />}
      </Pressable>
    </FadeIn>
  );
}

const styles = StyleSheet.create({
  row: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: tokens.spacing.sm,
    paddingVertical: tokens.spacing.sm,
    paddingHorizontal: tokens.spacing.sm,
    borderRadius: tokens.radius.md,
    backgroundColor: colors.surface.row,
  },
  rowPressed: {
    opacity: 0.78,
  },
  iconWrap: {
    width: 22,
    height: 22,
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
  body: {
    flex: 1,
    minWidth: 0,
    gap: 2,
  },
  name: {
    fontWeight: '600',
    fontSize: tokens.fontSize.smPlus,
  },
  metaRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    minWidth: 0,
  },
  metaText: {
    fontSize: tokens.fontSize.xs,
    color: colors.text.tertiary,
    flexShrink: 1,
  },
  metaDot: {
    fontSize: tokens.fontSize.xs,
    color: colors.text.tertiary,
  },
  pill: {
    flexShrink: 0,
    paddingHorizontal: tokens.spacing.sm,
    paddingVertical: 3,
    borderRadius: 999,
    borderWidth: 1,
    maxWidth: 96,
  },
  pillText: {
    fontSize: 10,
    fontWeight: '700',
    letterSpacing: 0.3,
    textTransform: 'uppercase',
  },
});
